import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import Input from '@/components/shared/molecules/Input';
import Button from '../shared/Button';
import { TCollection } from '@/interfaces/collections';
import { trpc } from '@/utils/trpc';
import { social_links } from '@prisma/client';
import LoadingSpinner from '../shared/LoadingSpinner';
import useNotification from '@/hooks/useNotification';
import InputLabel from '../shared/atoms/InputLabel';
import BrokenLinkIcon from './BrokenLinkIcon';
import clsx from 'clsx';
import sanitizeHtml from 'sanitize-html';
import XButton from '../shared/atoms/XButton';
import useEvent from '@/hooks/useEvent';
import { EVENTS } from '@/utils/constants';
import { captureException } from '@sentry/nextjs';

interface SocialLinkModalProps {
  collection: TCollection;
  selectedSocialLink: social_links & { bookmarkId: number };
  closeSelf: () => void;
  refetchCollectionData: () => void;
}

export default function SocialLinkModal({
  collection,
  selectedSocialLink,
  closeSelf,
  refetchCollectionData,
}: SocialLinkModalProps) {
  const [formProcessing, setFormProcesing] = useState(false);
  const [note, setNote] = useState(selectedSocialLink?.note ?? '');
  const [link, setLink] = useState(selectedSocialLink?.url ?? '');
  const [html, setHtml] = useState({ __html: '<div />' });
  const { capture } = useEvent();

  const addLinkMutation = trpc.collections.addSocialLinks.useMutation();
  const editLinkMutation = trpc.collections.editSocialLinks.useMutation();
  const removeLinkMutation = trpc.collections.removeSocialLinks.useMutation();
  const requestLinkMutation = trpc.iframely.requestLink.useMutation();

  const queryEnabled = !!link && link.length > 0;
  const {
    data: linkInfo,
    refetch,
    isLoading,
  } = trpc.iframely.getLinkInfo.useQuery(link, {
    enabled: queryEnabled,
  });

  const { data: linkThumbnail } = trpc.iframely.getThumbnail.useQuery(
    { link: link },
    {
      enabled: queryEnabled,
    }
  );

  const { showNotification } = useNotification();

  const formIsNotReady = useMemo(() => {
    return formProcessing || link.length <= 0 || !linkInfo || !linkInfo.html;
  }, [formProcessing, link, linkInfo]);

  const linkIsNotSupported = useMemo(() => {
    return link.length > 0 && !isLoading && (!linkInfo || !linkInfo.html);
  }, [link, linkInfo, isLoading]);

  useEffect(() => {
    if (linkInfo?.html) {
      const sanitizedHTML = sanitizeHtml(linkInfo.html, {
        allowedTags: ['b', 'i', 'em', 'strong', 'a', 'iframe', 'div'],
        allowedAttributes: {
          a: ['href'],
          iframe: ['src', 'width', 'height', 'allowfullscreen'],
          div: ['style', 'class'],
        },
      });
      setHtml({ __html: sanitizedHTML });
    }
  }, [linkInfo]);

  useEffect(() => {
    if (queryEnabled) {
      refetch();
    }
  }, [link, queryEnabled]);

  const handleNoteChange = useCallback((e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setNote(e.target.value);
  }, []);

  const handleLinkChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const linkValue = e.target.value.trim();
    setLink(linkValue);
  }, []);

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();
    try {
      setFormProcesing(true);
      const target = e.target as typeof e.target & {
        note: { value: string };
        link: { value: string };
      };
      const note = target.note?.value;
      const link = target.link?.value;

      if (selectedSocialLink?.bookmarkId !== 0) {
        await editLinkMutation.mutateAsync({
          id: selectedSocialLink.id,
          note: note,
          url: link,
          provider: linkThumbnail?.provider_name ?? '',
          width: linkThumbnail?.thumbnail_width,
          height: linkThumbnail?.thumbnail_height,
          bookmarkId: selectedSocialLink.bookmarkId,
        });
        showNotification({ message: 'Link updated' });
      } else {
        const res = await addLinkMutation.mutateAsync({
          note: note,
          collectionId: collection.id,
          url: link,
          provider: linkThumbnail?.provider_name ?? '',
          width: linkThumbnail?.thumbnail_width,
          height: linkThumbnail?.thumbnail_height,
        });
        showNotification({ message: 'Link added' });
        capture({
          event: EVENTS.addedLink,
          link_id: res?.socialLink?.id,
        });
      }
      await refetchCollectionData();
      closeSelf();
    } catch (e) {
      showNotification({ message: `Something went wrong: ${e.message}` }, 6000);
      captureException(e);
    } finally {
      setFormProcesing(false);
    }
  };

  const handleRemoveClick = useCallback(async () => {
    try {
      if (selectedSocialLink) {
        await removeLinkMutation.mutateAsync({
          id: selectedSocialLink?.id,
          bookmarkId: selectedSocialLink.bookmarkId,
        });
        showNotification({ message: 'Link removed' });
        closeSelf();
      }
    } catch (e) {
      showNotification({ message: `Something went wrong: ${e.message}` }, 6000);
    } finally {
    }
  }, [removeLinkMutation, selectedSocialLink, collection, closeSelf]);

  const handleRequestLinkClick = async () => {
    try {
      await requestLinkMutation.mutateAsync({ url: link, collection_id: collection.id });
      capture(
        {
          event: EVENTS.requestedLink,
          collection_uuid: collection.uuid ?? '',
          otherInfo: {
            uuid: collection.uuid ?? '',
            url: link,
          },
        },
        false
      );
      showNotification({ message: 'Link Requested' });
      closeSelf();
    } catch (e) {
      showNotification({ message: `Something went wrong: ${e.message}` }, 6000);
    }
  };

  const renderButton = useCallback(() => {
    if (selectedSocialLink.bookmarkId === 0) {
      return (
        <div className="grid gap-[10px] grid-cols-[auto_auto]">
          <Button
            type="primary"
            buttonType="submit"
            disabled={formIsNotReady}
            className={linkIsNotSupported ? 'place-self-start' : 'place-self-center col-span-2'}
            loading={formProcessing}
          >
            ADD LINK
          </Button>

          {linkIsNotSupported && (
            <Button
              onClick={handleRequestLinkClick}
              className="place-self-center"
              loading={requestLinkMutation.isLoading}
            >
              REQUEST SUPPORT
            </Button>
          )}
        </div>
      );
    } else {
      return (
        <div className="grid gap-[10px] grid-cols-[1fr_1fr]">
          <Button
            type="secondary"
            disabled={formProcessing || removeLinkMutation.isLoading}
            className="place-self-start"
            onClick={handleRemoveClick}
            loading={removeLinkMutation.isLoading}
          >
            REMOVE
          </Button>
          <Button
            type="primary"
            buttonType="submit"
            disabled={formIsNotReady || removeLinkMutation.isLoading}
            className="place-self-end"
            loading={formProcessing}
          >
            UPDATE
          </Button>
        </div>
      );
    }
  }, [
    selectedSocialLink,
    formProcessing,
    handleRemoveClick,
    linkIsNotSupported,
    handleRequestLinkClick,
  ]);

  const renderPreview = useCallback(() => {
    if (!queryEnabled) {
      return (
        <span className="font-adieu text-[14px] font-light uppercase leading-[15.4px] opacity-[0.3]">
          How do I look?
        </span>
      );
    }
    if (isLoading) {
      return <LoadingSpinner number={1} />;
    } else if (!!linkInfo) {
      return linkIsNotSupported ? (
        <BrokenLinkIcon />
      ) : (
        <>
          <div
            className={clsx(['social-link-frame h-full w-[377px] p-[15px] grid content-center'])}
            dangerouslySetInnerHTML={html}
          />
        </>
      );
    } else {
      return (
        <span className="font-adieu text-[14px] font-light uppercase leading-[15.4px] opacity-[0.3]">
          How do I look?
        </span>
      );
    }
  }, [isLoading, linkInfo, queryEnabled, linkIsNotSupported, html]);

  return (
    <div className="border-2 border-white px-[32px] py-[32px] w-[475px]">
      <XButton onClose={closeSelf} />
      <form onSubmit={handleSubmit} className="h-full flex flex-col justify-between gap-[15px]">
        <div className="grid gap-[8px]">
          <InputLabel>Note</InputLabel>
          <textarea
            name="note"
            value={note}
            onChange={handleNoteChange}
            placeholder="Tell us more"
            className="bg-black px-[16px] py-[9px] border border-white border-solid focus:outline-none focus:border-white focus:ring-0 font-nhg tracking-[0.48px] placeholder:text-white/40"
            rows={4}
            maxLength={300}
          />
          <span className="text-[12px] leading-[16.8px] tracking-[0.36px] mb-[16px]">
            ({note.length}/300 Characters)
          </span>
          <Input
            name="link"
            value={link}
            error={linkIsNotSupported ? 'Link not supported' : undefined}
            label="LINK"
            onChange={handleLinkChange}
            labelGap={8}
            inputClass="normal-case"
            placeholder="Enter website here"
            errorAlignLeft={true}
            inputFont="font-nhg tracking-[0.48px] !text-[16px]"
          />
        </div>
        <div className="grid gap-[15px] mt-[9px]">
          <div className="w-full grid gap-[8px]">
            <span className="font-adieu font-light text-[14px] leading-[19.6px] tracking-[2.1px]">
              PREVIEW
            </span>
            <div className="border p-[16px] flex items-center justify-center relative w-full h-full">
              <div className="h-[262px] w-full flex items-center justify-center">
                {renderPreview()}
              </div>
            </div>
          </div>
          {renderButton()}
        </div>
      </form>
    </div>
  );
}
